import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { toast } from '@utima/ui';

import { getErrorTranslations } from '@/locale/error-translations';

type ErrorResponseData = {
  code: string;
  path: string;
  httpStatus: number;
  stack?: string;
};

function toastError(error: { title: string; message: string }) {
  toast.error(error.title, {
    description: error.message,
  });
}

/**
 * Default global app error handler.
 */
function defaultErrorHandler(error: unknown) {
  const errorTranslations = getErrorTranslations();

  if (error instanceof TRPCClientError) {
    const { data } = error;
    const { httpStatus, errorIdentifier } =
      (data as ErrorResponseData & {
        errorIdentifier: string;
      }) || {};

    return toastError(
      (errorIdentifier &&
        errorIdentifier in errorTranslations &&
        errorTranslations[errorIdentifier as never]) ||
        (httpStatus &&
          httpStatus in errorTranslations &&
          errorTranslations[httpStatus as never]) ||
        errorTranslations.generic,
    );
  }

  return toastError(errorTranslations.generic);
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      return defaultErrorHandler(error);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      return defaultErrorHandler(error);
    },
  }),
  defaultOptions: {
    mutations: {},
    queries: {
      staleTime: 30000,
      retry: false,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});
